import { isApiError } from '@pa/api'
import { getAppointmentPfLink } from '@pa/api/clinic'
import { useCallback, useState } from 'react'

/** UserFlows C-01 / C-01a (task 23, D-80). */
export const OPEN_IN_PF = 'Open in Practice Fusion'
/** C-01's column heading for the row button (2026-10-16). */
export const OPEN_IN_PF_COLUMN = 'Open in PF'
export const OPENING = 'Opening…'
export const PF_NOT_FOUND = 'This appointment wasn’t found in Practice Fusion. It may have been deleted there.'
export const PF_UNREACHABLE = 'Practice Fusion isn’t reachable right now. Try again in a minute.'

/**
 * #71's `pf_appointment_not_found` (deleted in PF) or `not_found` (gone here) → "not found"; anything else (PF Keeper
 * down or outdated, `pf_error`, a network error) → "unreachable".
 */
export function openErrorText(err: unknown): string {
  return isApiError(err) && (err.code === 'pf_appointment_not_found' || err.code === 'not_found')
    ? PF_NOT_FOUND
    : PF_UNREACHABLE
}

/**
 * Opens the patient's Practice Fusion chart (#71) in a new tab without tripping the popup blocker: a blank tab is
 * opened synchronously inside the click, then sent to the URL once #71 answers (`opener` cleared first). On a failure
 * the blank tab is closed. Nothing is cached: every click asks again. Resolves to `null` when the tab was sent, else
 * the message to show. The URL holds the patient's PF id: it is never stored, logged or put in a message.
 */
export async function openInPf(clinicId: string, appointmentId: string): Promise<string | null> {
  const tab = window.open('about:blank', '_blank')
  try {
    const { url } = await getAppointmentPfLink(clinicId, appointmentId)
    // The API only builds https PF URLs; anything else is never navigated to.
    if (!url.startsWith('https://')) throw new Error('unexpected PF link')
    if (tab) {
      tab.opener = null
      tab.location.replace(url)
    } else {
      // The browser refused even the blank tab: one direct try.
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    return null
  } catch (err) {
    tab?.close()
    return openErrorText(err)
  }
}

/**
 * The click flow for one list (the C-01 rows) or the drawer: `open(id)` must be called from the click handler itself
 * (the blank tab opens before anything is awaited). `opening` is the appointment being opened ("Opening…", disabled).
 */
export function useOpenInPf(clinicId: string, onError: (message: string) => void) {
  const [opening, setOpening] = useState<string | null>(null)
  const open = useCallback(
    (appointmentId: string) => {
      setOpening(appointmentId)
      void openInPf(clinicId, appointmentId).then((message) => {
        setOpening(null)
        if (message !== null) onError(message)
      })
    },
    [clinicId, onError],
  )
  return { opening, open }
}
